import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, client, persistSession, restoreSession, currentSessionId } from '@/api/client';
import { setLocale } from '@/i18n';
import { applyTheme } from '@/composables/useTheme';
import { releaseCompanyBranding } from '@/composables/useCompanyBranding';
import { unregisterMobilePush } from '@/mobile-push';
import type { AuthResponse, AuthUser, Membership, Session, UserProfile } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserProfile | null>(null);
  const memberships = ref<Membership[]>([]);
  const permissions = ref<Set<string>>(new Set());
  const ready = ref(false);
  const loading = ref(false);
  let bootstrapping: Promise<void> | null = null;

  const isAuthenticated = computed(() => !!user.value);
  const sessionId = computed(() => currentSessionId());

  function can(permission: string): boolean {
    if (!user.value) return false;
    if (user.value.isSuperAdmin) return true;
    return permissions.value.has('*') || permissions.value.has(permission);
  }

  function canAny(...candidates: string[]): boolean {
    return candidates.some((permission) => can(permission));
  }

  async function applyIdentity(profile: UserProfile): Promise<void> {
    user.value = profile;
    applyTheme(profile.theme);
    await setLocale(profile.locale, { persist: true });
  }

  /** Restores a saved session on boot; safe to call on every app start. */
  async function bootstrap(): Promise<void> {
    if (ready.value) return;
    // `App.vue` and the router guard both call this before the first paint,
    // and `ready` only flips once the restore has finished — so without a
    // shared promise the session is restored twice, in parallel.
    bootstrapping ??= runBootstrap().finally(() => {
      bootstrapping = null;
    });
    return bootstrapping;
  }

  async function runBootstrap(): Promise<void> {
    loading.value = true;
    try {
      const restored = await restoreSession();
      if (restored) {
        const profile = await api.get<UserProfile>('users/me');
        await applyIdentity(profile);
        memberships.value = await api.get<Membership[]>('users/me/memberships');
      }
    } catch {
      await persistSession(null);
      user.value = null;
    } finally {
      ready.value = true;
      loading.value = false;
    }
  }

  async function login(email: string, password: string, rememberMe = false): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('auth/login', { email, password, rememberMe });
    if (response.tokens) {
      await persistSession({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        sessionId: response.tokens.sessionId,
      });
      const profile = await api.get<UserProfile>('users/me');
      await applyIdentity(profile);
      memberships.value = await api.get<Membership[]>('users/me/memberships');
    }
    return response;
  }

  async function register(payload: {
    email: string;
    password: string;
    displayName: string;
    locale?: string;
    timezone?: string;
    invitationToken?: string;
  }): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('auth/register', {
      ...payload,
      timezone: payload.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (response.tokens) {
      await persistSession({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        sessionId: response.tokens.sessionId,
      });
      const profile = await api.get<UserProfile>('users/me');
      await applyIdentity(profile);
      memberships.value = await api.get<Membership[]>('users/me/memberships');
    }
    return response;
  }

  async function verifyEmail(token: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('auth/verify-email', { token });
    if (response.tokens) {
      await persistSession({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        sessionId: response.tokens.sessionId,
      });
      const profile = await api.get<UserProfile>('users/me');
      await applyIdentity(profile);
    }
    return response;
  }

  async function logout(): Promise<void> {
    try {
      await unregisterMobilePush();
    } catch {
      // The FCM token expires locally even if the server is temporarily down.
    }
    try {
      await api.post('auth/logout');
    } catch {
      // Signing out locally matters more than the server round-trip.
    }
    await persistSession(null);
    user.value = null;
    memberships.value = [];
    permissions.value = new Set();
    releaseCompanyBranding();
  }

  /** Loads the caller's effective permissions for a company. */
  async function loadPermissions(companyId: string): Promise<void> {
    const response = await api.get<{ permissions: string[] }>(`companies/${companyId}/my-permissions`);
    permissions.value = new Set(response.permissions);
  }

  async function updateProfile(patch: Partial<UserProfile>): Promise<void> {
    const profile = await api.patch<UserProfile>('users/me', patch);
    await applyIdentity(profile);
  }

  async function sessions(): Promise<Session[]> {
    return api.get<Session[]>('auth/sessions');
  }

  async function revokeSession(id: string): Promise<void> {
    await api.delete(`auth/sessions/${id}`);
  }

  async function revokeOtherSessions(): Promise<number> {
    const response = await api.delete<{ revoked: number }>('auth/sessions');
    return response.revoked;
  }

  async function changePassword(currentPassword: string, newPassword: string, revokeOthers = true): Promise<void> {
    await api.post('auth/change-password', { currentPassword, newPassword, revokeOtherSessions: revokeOthers });
  }

  function handleExternalSignOut(): void {
    user.value = null;
    memberships.value = [];
    permissions.value = new Set();
    releaseCompanyBranding();
  }

  return {
    user,
    memberships,
    permissions,
    ready,
    loading,
    isAuthenticated,
    sessionId,
    can,
    canAny,
    bootstrap,
    login,
    register,
    verifyEmail,
    logout,
    loadPermissions,
    updateProfile,
    sessions,
    revokeSession,
    revokeOtherSessions,
    changePassword,
    handleExternalSignOut,
    realtime: client.realtime,
  };
});
